import { runOnDomReady } from '../../core/dom-ready.ts';
import { initDelegatedPasswordToggles } from '../../modules/password-toggle.js';
import { validarForcaSenha } from '../../modules/password-policy.js';
import { FormsModule } from '../../modules/forms.js';
import AlertsModule from '../../modules/alerts.js';
import { logger } from '../../services/logger.js';

const SUBMIT_GUARD_ATTR = 'data-submitting';

function init(): void {
  initDelegatedPasswordToggles();
  setupRedefinirForm();
  setupEsqueciForm();
}

function setupRedefinirForm(): void {
  const form = document.getElementById('formRedefinirSenha') as HTMLFormElement | null;
  if (!form) {
    return;
  }

  form.addEventListener('submit', async (e) => {
    e.preventDefault();

    const senha = (document.getElementById('senha') as HTMLInputElement | null)?.value ?? '';
    const confirmarSenha =
      (document.getElementById('confirmar_senha') as HTMLInputElement | null)?.value ?? '';

    const erroForca = validarForcaSenha(senha);
    if (erroForca) {
      void AlertsModule.error(erroForca);
      return;
    }
    if (senha !== confirmarSenha) {
      void AlertsModule.error('As senhas não coincidem.');
      return;
    }

    // Toast de sucesso aqui porque não há tela de confirmação: redireciona para o login.
    const ok = await submitForm(form, 'api/password-reset.php', {
      successToast: 'Senha redefinida com sucesso!',
    });
    if (ok) {
      window.setTimeout(() => window.location.assign('login.php'), 1200);
    }
  });
}

function setupEsqueciForm(): void {
  const form = document.getElementById('formEsqueciSenha') as HTMLFormElement | null;
  if (!form) {
    return;
  }

  const formView = document.getElementById('esqueciFormView');
  const sentView = document.getElementById('esqueciSentView');

  document.querySelector<HTMLButtonElement>('[data-reenviar]')?.addEventListener('click', () => {
    if (sentView) {
      sentView.hidden = true;
    }
    if (formView) {
      formView.hidden = false;
    }
    form.reset();
    form.classList.remove('was-validated');
    (document.getElementById('email') as HTMLInputElement | null)?.focus();
  });

  form.addEventListener('submit', async (e) => {
    e.preventDefault();

    const email = (document.getElementById('email') as HTMLInputElement | null)?.value.trim() ?? '';
    if (!FormsModule.validators.email(email)) {
      void AlertsModule.error('Informe um e-mail válido.');
      return;
    }

    const enviado = await submitForm(form, 'api/password-reset.php', {
      successToast: 'Link de redefinição enviado.',
    });

    if (enviado && formView && sentView) {
      formView.hidden = true;
      sentView.hidden = false;
    }
  });
}

interface SubmitOptions {
  /** Mensagem do toast de sucesso. Omitir quando a própria tela já confirma. */
  successToast?: string;
}

/** @returns `true` se o backend confirmou sucesso. */
async function submitForm(
  form: HTMLFormElement,
  url: string,
  { successToast }: SubmitOptions
): Promise<boolean> {
  if (form.getAttribute(SUBMIT_GUARD_ATTR) === '1') {
    return false;
  }
  form.setAttribute(SUBMIT_GUARD_ATTR, '1');

  const submitBtn = form.querySelector<HTMLButtonElement>('[data-reset-submit]');
  const originalLabel = submitBtn?.querySelector('span')?.textContent ?? '';

  try {
    if (submitBtn) {
      submitBtn.disabled = true;
      submitBtn.querySelector('span')!.textContent = 'Aguarde...';
    }

    const response = await fetch(url, {
      method: 'POST',
      body: new FormData(form),
      credentials: 'same-origin',
    });

    const data = (await response.json()) as { success?: boolean; message?: string };

    if (data.success) {
      if (successToast) {
        void AlertsModule.success(successToast);
      }
      return true;
    }

    void AlertsModule.error(data.message ?? 'Ocorreu um erro. Tente novamente.');
    return false;
  } catch (err) {
    logger.error('[RedefinirSenha] Erro ao submeter formulário:', err);
    void AlertsModule.error('Erro de comunicação com o servidor. Tente novamente.');
    return false;
  } finally {
    if (submitBtn) {
      submitBtn.disabled = false;
      submitBtn.querySelector('span')!.textContent = originalLabel;
    }
    form.removeAttribute(SUBMIT_GUARD_ATTR);
  }
}

runOnDomReady(init);
